import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { projectApi, Project } from '../services/projectApi';
import { useAuthStore } from '../store/authStore';
import { toast, confirm } from '../store/toastStore';
import PageHeader from '../components/layout/PageHeader';
import EmptyState from '../components/layout/EmptyState';
import Badge from '../components/shared/Badge';
import { SkeletonCard } from '../components/shared/Skeleton';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'generating', label: '生成中' },
  { value: 'completed', label: '已完成' },
  { value: 'archived', label: '已归档' },
];

const statusBadge = (status: string) => {
  const map: Record<string, { variant: 'default' | 'warning' | 'success' | 'error'; label: string }> = {
    draft: { variant: 'default', label: '草稿' },
    generating: { variant: 'warning', label: '生成中' },
    completed: { variant: 'success', label: '已完成' },
    archived: { variant: 'error', label: '已归档' },
  };
  const s = map[status] || { variant: 'default' as const, label: status };
  return <Badge variant={s.variant}>{s.label}</Badge>;
};

const ProjectList: React.FC = () => {
  const token = useAuthStore((state) => state.token);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadProjects = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await projectApi.getProjects({
        page, page_size: 20,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
      });
      setProjects(response.data.projects || []);
      setTotalPages(Math.ceil(response.data.total / 20));
    } catch (error: any) {
      if (error.response?.status !== 401) { setProjects([]); setTotalPages(1); }
    } finally { setLoading(false); }
  }, [token, page, statusFilter, searchQuery]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleDelete = async (id: number) => {
    if (!await confirm({ title: '确认删除', message: '确定要删除这个项目吗？', confirmLabel: '删除', danger: true })) return;
    try { await projectApi.deleteProject(id); toast.success('已删除'); loadProjects(); }
    catch { toast.error('删除失败'); }
  };

  return (
    <div>
      <PageHeader
        title="我的项目"
        subtitle="管理您的标书项目，创建、编辑或导出"
        actions={
          <Link
            to="/projects/new"
            className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-foreground bg-card border border-border rounded-lg hover:border-primary hover:text-primary hover:shadow-sm transition-all duration-150"
          >
            新建项目
          </Link>
        }
      />

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 bg-accent rounded-lg p-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setPage(1); }}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === opt.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input !pl-9 !w-56"
            placeholder="搜索项目..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<MagnifyingGlassIcon className="w-7 h-7" />}
          title={searchQuery ? '未找到匹配的项目' : '还没有项目'}
          description={searchQuery ? '试试其他关键词' : '创建您的第一个标书项目'}
          action={
            <Link
              to="/projects/new"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-foreground bg-card border border-border rounded-lg hover:border-primary hover:text-primary hover:shadow-sm transition-all duration-150"
            >
              新建项目
            </Link>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            {projects.map((project: any) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="card hover:shadow-card-hover transition-shadow p-5 group"
              >
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate min-w-0">
                    {project.project_name}
                  </h3>
                  <span className="shrink-0 whitespace-nowrap">{statusBadge(project.status)}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  {project.created_at ? new Date(project.created_at).toLocaleDateString('zh-CN') : '-'}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  {project.proposal_mode !== 'concise' && (
                    <div className="flex-1 h-1.5 bg-accent rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all bg-primary"
                        style={{
                          width: `${project.expected_word_count ? Math.min(100, ((project.current_word_count || 0) / project.expected_word_count) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  )}
                  <span className="text-xs text-muted-foreground">{project.current_word_count || 0} 字</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-8">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    page === p ? 'bg-brand-700 text-white' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectList;
